import type { AccountOnboarding } from '@superfunnel/types';
import { doc, getDoc, setDoc, Timestamp, updateDoc } from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { db } from '../Firebase';

const STORAGE_KEY = 'superfunnel_onboarding';
const ACCOUNT_ID_KEY = 'superfunnel_onboarding_accountId';
const ACCOUNT_SLUG_KEY = 'superfunnel_onboarding_slug';

export function useOnboarding(initialAccountId?: string) {
  const [accountId, setAccountId] = useState<string | undefined>(() => {
    // Use initialAccountId if provided (from route param)
    if (initialAccountId) {
      return initialAccountId;
    }
    // Otherwise, restore from session storage on mount
    try {
      const cached = sessionStorage.getItem(ACCOUNT_ID_KEY);
      if (cached) {
        return cached;
      }
    } catch (err) {
      console.error('Error restoring accountId from session storage:', err);
    }
    return undefined;
  });
  const [accountSlug, setAccountSlug] = useState<string | undefined>(() => {
    // Restore from session storage on mount
    try {
      const cached = sessionStorage.getItem(ACCOUNT_SLUG_KEY);
      if (cached) {
        return cached;
      }
    } catch (err) {
      console.error('Error restoring accountSlug from session storage:', err);
    }
    return undefined;
  });
  const [onboarding, setOnboarding] = useState<AccountOnboarding | undefined>(
    () => {
      // Restore from session storage on mount
      try {
        const cached = sessionStorage.getItem(STORAGE_KEY);
        if (cached) {
          return JSON.parse(cached) as AccountOnboarding;
        }
      } catch (err) {
        console.error('Error restoring onboarding from session storage:', err);
      }
      return undefined;
    },
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const [hasFetched, setHasFetched] = useState(false);

  // Cache accountId to session storage whenever it changes
  useEffect(() => {
    if (accountId) {
      try {
        sessionStorage.setItem(ACCOUNT_ID_KEY, accountId);
      } catch (err) {
        console.error('Error caching accountId to session storage:', err);
      }
    } else {
      sessionStorage.removeItem(ACCOUNT_ID_KEY);
    }
  }, [accountId]);

  // Cache onboarding data to session storage whenever it changes
  useEffect(() => {
    if (onboarding) {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(onboarding));
      } catch (err) {
        console.error('Error caching onboarding to session storage:', err);
      }
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [onboarding]);

  // Single fetch on mount/accountId change (for page refreshes)
  useEffect(() => {
    if (!accountId) {
      setLoading(false);
      return;
    }

    // Only fetch once per accountId
    if (hasFetched) {
      return;
    }

    const fetchOnboarding = async () => {
      try {
        const onboardingRef = doc(
          db,
          'accounts',
          accountId,
          'accountOnboarding',
          'current',
        );

        const snapshot = await getDoc(onboardingRef);
        if (snapshot.exists()) {
          setOnboarding(snapshot.data() as AccountOnboarding);
        }
        // If it doesn't exist, keep onboarding as undefined
        // It will be set manually after account creation
      } catch (err) {
        console.error('Error fetching onboarding:', err);
        setError(err as Error);
      } finally {
        setLoading(false);
        setHasFetched(true);
      }
    };

    fetchOnboarding();
  }, [accountId, hasFetched]);

  // Reset hasFetched when accountId changes
  useEffect(() => {
    setHasFetched(false);
  }, [accountId]);

  const moveToStep = useCallback(
    async (
      stepId: string,
      stepIndex: number,
      stepData?: Partial<AccountOnboarding>,
    ) => {
      if (!accountId || !onboarding) return;

      const onboardingRef = doc(
        db,
        'accounts',
        accountId,
        'accountOnboarding',
        'current',
      );

      // Add current step to completed steps if not already there
      const updatedCompletedSteps = [
        ...new Set([...onboarding.completedSteps, onboarding.currentStep]),
      ];

      setOnboarding((prev) =>
        prev
          ? {
              ...prev,
              currentStep: stepId,
              currentStepIndex: stepIndex,
              completedSteps: updatedCompletedSteps,
              ...stepData,
              lastUpdatedAt: Timestamp.now(),
            }
          : prev,
      );

      await updateDoc(onboardingRef, {
        ...stepData,
        currentStep: stepId,
        currentStepIndex: onboarding.currentStepIndex + 1,
        completedSteps: updatedCompletedSteps,
        lastUpdatedAt: Timestamp.now(),
      });
    },
    [accountId, onboarding],
  );

  // Initialize onboarding document for accounts that don't have one yet
  // This is used when resuming onboarding for existing accounts
  const initializeOnboarding = useCallback(
    async (websiteUrl: string, startStep: string = 'profile') => {
      if (!accountId) return;

      const onboardingRef = doc(
        db,
        'accounts',
        accountId,
        'accountOnboarding',
        'current',
      );

      const startStepIndex =
        ['website', 'scanning', 'profile', 'ad-platforms', 'referral'].indexOf(
          startStep,
        ) + 1;

      const initialOnboarding: AccountOnboarding = {
        websiteUrl: websiteUrl,
        currentStep: startStep,
        currentStepIndex: startStepIndex,
        completedSteps: [],
        startedAt: Timestamp.now(),
        lastUpdatedAt: Timestamp.now(),
        status: 'in-progress',
      };

      await setDoc(onboardingRef, initialOnboarding);
      setOnboarding(initialOnboarding);
    },
    [accountId],
  );

  return {
    onboarding,
    setOnboarding,
    loading,
    error,
    moveToStep,
    initializeOnboarding,
    accountId,
    setAccountId,
    accountSlug,
    setAccountSlug,
  };
}
